import { makeRequest } from './request';

export interface ModelCatalog {
  tts: Record<string, {
    name: string;
    description: string;
    voices?: string[];
    languages?: string[];
    pricing: string;
  }>;
  stt: Record<string, {
    name: string;
    description: string;
    languages?: string[];
    pricing: string;
  }>;
  llm: Record<string, {
    name: string;
    description: string;
    pricing: string;
  }>;
  lastUpdated: string;
}

class CatalogManager {
  private catalog: ModelCatalog | null = null;
  private lastFetch: number = 0;
  private cacheTimeout = 24 * 60 * 60 * 1000; // 24 hours cache in memory

  private async fetchCatalog(): Promise<ModelCatalog> {
    try {
      // Semi-private endpoint - not documented but accessible
      // Obfuscate the endpoint slightly
      const endpoint = Buffer.from('L2FscGhhL2NhdGFsb2c=', 'base64').toString();
      
      const response = await fetch(`https://api.slng.ai${endpoint}`, {
        headers: {
          'X-SDK-Version': '1.0.0',
          'X-SDK-Client': 'voiceai-sdk',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch catalog');
      }

      return await response.json();
    } catch (error) {
      // Fallback to embedded defaults if endpoint unavailable
      return this.getDefaultCatalog();
    }
  }

  private getDefaultCatalog(): ModelCatalog {
    return {
      tts: {
        'orpheus': {
          name: 'Orpheus',
          description: 'Frontier TTS model with emotive speech',
          voices: ['tara', 'leah', 'jess', 'leo', 'dan', 'mia', 'zac', 'zoe'],
          languages: ['en', 'fr', 'de', 'ko', 'zh', 'es', 'it', 'hi'],
          pricing: '$0.30 per 1M characters'
        },
        'orpheus-indic': {
          name: 'Orpheus Indic',
          description: 'Optimized for 8 major Indian languages, hosted in Mumbai',
          voices: ['hindi_male', 'hindi_female', 'tamil_male', 'tamil_female', 'telugu_male', 'bengali_female', 'marathi_male', 'gujarati_female', 'kannada_male', 'malayalam_female'],
          languages: ['hi', 'ta', 'te', 'bn', 'mr', 'gu', 'kn', 'ml'],
          pricing: '$0.04 per minute generated'
        },
        'elevenlabs/multi-v2': {
          name: 'ElevenLabs Multi-v2',
          description: 'Multilingual model supporting 29+ languages',
          voices: ['Rachel', 'Drew', 'Clyde', 'Paul', 'Domi', 'Dave', 'Fin', 'Bella'],
          languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'hi', 'ar', 'zh', 'ja', 'ko'],
          pricing: '$0.30 per 1M characters'
        },
        'elevenlabs/turbo-v2-5': {
          name: 'ElevenLabs Turbo v2.5',
          description: 'Ultra-fast TTS with low latency',
          voices: ['Rachel', 'Drew', 'Clyde', 'Paul'],
          languages: ['en', 'es', 'fr', 'de', 'it', 'pt'],
          pricing: '$0.25 per 1M characters'
        },
        'elevenlabs/v3': {
          name: 'ElevenLabs v3',
          description: 'Latest generation with high-quality voices',
          voices: ['Rachel', 'Drew', 'Clyde', 'Paul', 'Domi', 'Dave', 'Fin', 'Bella'],
          languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'hi'],
          pricing: '$0.35 per 1M characters'
        },
        'elevenlabs/flash-v2-5': {
          name: 'ElevenLabs Flash v2.5',
          description: 'Fast model with good quality balance',
          voices: ['Rachel', 'Drew', 'Clyde', 'Paul'],
          languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl'],
          pricing: '$0.20 per 1M characters'
        },
        'koroko': {
          name: 'Koroko',
          description: 'Efficient 82M parameter model',
          languages: ['en'],
          pricing: '$0.15 per 1M characters'
        },
        'vui': {
          name: 'VUI',
          description: 'Fast, reliable baseline model',
          voices: ['default'],
          languages: ['en'],
          pricing: '$0.10 per 1M characters'
        },
        'xtts-v2': {
          name: 'XTTS-v2',
          description: 'Voice cloning in 17 languages',
          languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'tr', 'ru', 'nl', 'cs', 'ar', 'zh', 'ja', 'ko', 'hu', 'hi'],
          pricing: '$0.50 per 1M characters'
        },
        'mars6': {
          name: 'MARS6',
          description: 'Voice + prosody cloning',
          languages: ['en-us', 'fr-fr', 'de-de', 'es-es', 'it-it', 'pt-pt', 'zh-cn', 'ja-jp', 'ko-kr', 'nl-nl'],
          pricing: '$0.60 per 1M characters'
        },
        'twi-speecht5': {
          name: 'Twi SpeechT5',
          description: 'Specialized for Twi language',
          languages: ['tw'],
          pricing: '$0.25 per 1M characters'
        }
      },
      stt: {
        'whisper-v3': {
          name: 'Whisper-v3',
          description: 'OpenAI\'s best speech recognition',
          languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'zh', 'ja', 'ko', 'ar', 'hi'],
          pricing: '$0.006 per minute'
        },
        'kyutai': {
          name: 'Kyutai Streaming',
          description: 'High-performance streaming STT for French/English, hosted in Mumbai',
          languages: ['en', 'fr'],
          pricing: '4 credits per minute'
        }
      },
      llm: {
        'llama-4-scout': {
          name: 'Llama 4 Scout',
          description: 'Fast, capable 17B parameter model',
          pricing: '$0.10 per 1M tokens'
        }
      },
      lastUpdated: new Date().toISOString()
    };
  }

  async getCatalog(): Promise<ModelCatalog> {
    // Use in-memory cache for 24 hours
    const now = Date.now();
    if (this.catalog && (now - this.lastFetch) < this.cacheTimeout) {
      return this.catalog;
    }

    // Fetch new catalog
    this.catalog = await this.fetchCatalog();
    this.lastFetch = now;
    
    return this.catalog;
  }

  async getModels(): Promise<string[]> {
    const catalog = await this.getCatalog();
    return [
      ...Object.keys(catalog.tts),
      ...Object.keys(catalog.stt),
      ...Object.keys(catalog.llm)
    ];
  }

  async getPricing(): Promise<Record<string, string>> {
    const catalog = await this.getCatalog();
    const pricing: Record<string, string> = {};
    
    Object.entries(catalog.tts).forEach(([key, model]) => {
      pricing[`tts/${key}`] = model.pricing;
    });
    
    Object.entries(catalog.stt).forEach(([key, model]) => {
      pricing[`stt/${key}`] = model.pricing;
    });
    
    Object.entries(catalog.llm).forEach(([key, model]) => {
      pricing[`llm/${key}`] = model.pricing;
    });
    
    return pricing;
  }

  // Force refresh the catalog
  async refresh(): Promise<void> {
    this.catalog = null;
    this.lastFetch = 0;
    await this.getCatalog();
  }
}

export const catalogManager = new CatalogManager();